import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Eye, Save, X, FileText, BookOpen,
  Calendar, Tag, Upload, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const KnowledgeHubAdminPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    category: 'law_update',
    summary: '',
    content: '',
    tags: '',
    featured: false,
    author: ''
  });

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, []);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/knowledge/articles?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/knowledge/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCreateNew = () => {
    setEditingArticle(null);
    setFormData({
      title: '',
      category: 'law_update',
      summary: '',
      content: '',
      tags: '',
      featured: false,
      author: ''
    });
    setShowEditor(true);
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      category: article.category,
      summary: article.summary,
      content: article.content,
      tags: article.tags ? article.tags.join(', ') : '',
      featured: article.featured || false,
      author: article.author
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!formData.title || !formData.summary || !formData.content) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const articleData = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        publishedDate: editingArticle?.publishedDate || new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString().split('T')[0],
        views: editingArticle?.views || 0
      };

      // Note: This is a placeholder - actual implementation would use PUT/POST endpoints
      // For now, we'll use the seed endpoint to create sample data
      
      setSuccess(editingArticle ? 'Article updated successfully!' : 'Article created successfully!');
      setShowEditor(false);
      setTimeout(() => {
        fetchArticles();
        setSuccess(null);
      }, 2000);

    } catch (error) {
      console.error('Error saving article:', error);
      setError('Failed to save article');
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) {
      return;
    }

    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Note: This is a placeholder - actual implementation would use DELETE endpoint
      
      setSuccess('Article deleted successfully!');
      fetchArticles();
      setTimeout(() => setSuccess(null), 3000);

    } catch (error) {
      console.error('Error deleting article:', error);
      setError('Failed to delete article');
    }
  };

  const handleSeedData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/knowledge/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setSuccess('Sample articles created successfully!');
        fetchArticles();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to seed data. Admin access required.');
      }
    } catch (error) {
      console.error('Error seeding data:', error);
      setError('An error occurred while seeding data');
    }
  };

  const getCategoryBadgeColor = (category) => {
    switch (category) {
      case 'law_update': return 'bg-blue-100 text-blue-800';
      case 'template': return 'bg-green-100 text-green-800';
      case 'case_study': return 'bg-purple-100 text-purple-800';
      case 'training': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/xchange/admin/panel')}
            className="text-teal-600 hover:text-teal-700 mb-4 flex items-center"
          >
            ← Back to Admin Panel
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BookOpen className="w-8 h-8 mr-3 text-teal-600" />
                Knowledge Hub Admin
              </h1>
              <p className="text-gray-600 mt-1">Manage articles, templates, and knowledge base content</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleSeedData}
                variant="outline"
                className="text-gray-600 border-gray-300"
              >
                <Upload className="w-5 h-5 mr-2" />
                Seed Sample Data
              </Button>
              <Button
                onClick={handleCreateNew}
                className="text-white"
                style={{ background: '#14B8A6' }}
              >
                <Plus className="w-5 h-5 mr-2" />
                Create New Article
              </Button>
            </div>
          </div>
        </div>

        {success && <AlertBanner type="success" message={success} className="mb-6" />}
        {error && <AlertBanner type="error" message={error} className="mb-6" />}

        {/* Editor Modal */}
        {showEditor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingArticle ? 'Edit Article' : 'Create New Article'}
                </h2>
                <button
                  onClick={() => setShowEditor(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter article title"
                  />
                </div>

                {/* Category & Author */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="law_update">Law Update</option>
                      <option value="template">Template</option>
                      <option value="case_study">Case Study</option>
                      <option value="training">Training</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author *
                    </label>
                    <input
                      type="text"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="e.g., TMG Legal Team"
                    />
                  </div>
                </div>

                {/* Summary */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Summary *
                  </label>
                  <textarea
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    rows="3"
                    placeholder="Brief summary of the article (shown in listings)"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g., CSR Law, Compliance, 2024"
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content * (Markdown supported)
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                    rows="15"
                    placeholder="Enter article content in markdown format..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Tip: Use markdown for formatting. Example: # Heading, **bold**, *italic*, - bullet points
                  </p>
                </div>

                {/* Featured Toggle */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="mr-3 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="text-sm font-medium text-gray-700">
                    Mark as Featured Article
                  </label>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex justify-end gap-3">
                <Button
                  onClick={() => setShowEditor(false)}
                  variant="outline"
                  className="text-gray-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  className="text-white"
                  style={{ background: '#14B8A6' }}
                >
                  <Save className="w-5 h-5 mr-2" />
                  {editingArticle ? 'Update Article' : 'Create Article'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {categories.map(cat => (
            <div key={cat.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <FileText className="w-8 h-8 text-teal-600" />
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadgeColor(cat.id)}`}>
                  {cat.name}
                </span>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">{cat.articleCount}</p>
              <p className="text-sm text-gray-600">Articles</p>
            </div>
          ))}
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">All Articles</h2>
          </div>

          {articles.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Articles Yet</h3>
              <p className="text-gray-500 mb-4">Create your first article or seed sample data</p>
              <Button
                onClick={handleCreateNew}
                className="text-white"
                style={{ background: '#14B8A6' }}
              >
                <Plus className="w-5 h-5 mr-2" />
                Create First Article
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {articles.map(article => (
                <div key={article.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadgeColor(article.category)}`}>
                          {article.category.replace('_', ' ').toUpperCase()}
                        </span>
                        {article.featured && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            ⭐ Featured
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {article.summary}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(article.publishedDate).toLocaleDateString()}
                        </span>
                        <span className="flex items-center">
                          <Eye className="w-4 h-4 mr-1" />
                          {article.views} views
                        </span>
                        <span>By {article.author}</span>
                      </div>
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {article.tags.slice(0, 4).map(tag => (
                            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => navigate(`/knowledge/article/${article.id}`)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="View"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(article)}
                        className="p-2 text-gray-400 hover:text-teal-600 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeHubAdminPanel;
